import { useState, useEffect, useCallback, useRef } from "react";
import axiosInstance from "../../api/axiosInstance";

const TOAST_DURATION = 4000;

const designationApi = {
  get: (vendorProfileId) =>
    axiosInstance.get(`/api/superadmin/vendors/${vendorProfileId}/contact-person-designations/`),

  update: (vendorProfileId, designationIds) =>
    axiosInstance.post(`/api/superadmin/vendors/${vendorProfileId}/contact-person-designations/`, { designation_ids: designationIds }),

  delete: (vendorProfileId) =>
    axiosInstance.delete(`/api/superadmin/vendors/${vendorProfileId}/contact-person-designations/0/`),

  search: (q = "") =>
    axiosInstance.get(`/api/vendors/designations/?q=${encodeURIComponent(q)}`),
};

const CloseIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const LoaderIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    style={{ animation: "dg-spin 0.8s linear infinite", flexShrink: 0 }}>
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
  </svg>
);
const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);
const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const RefreshIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);
const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const ChevronDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const AlertTriangleIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);
const UserIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

let _dgToastSetter = null;
const toast = {
  _push: (item) => {
    const id = Date.now() + Math.random();
    _dgToastSetter?.((p) => [...p, { id, ...item }]);
    return id;
  },
  success: (title, msg) => toast._push({ type: "success", title, msg }),
  error:   (title, msg) => toast._push({ type: "error",   title, msg }),
  info:    (title, msg) => toast._push({ type: "info",    title, msg }),
  loading: (title)      => toast._push({ type: "loading", title, persistent: true }),
  dismiss: (id)         => _dgToastSetter?.((p) => p.filter((t) => t.id !== id)),
};

const TOAST_META = {
  success: { bar: "#16a34a", icon: "✓" },
  error:   { bar: "#dc2626", icon: "✕" },
  info:    { bar: "#1d4ed8", icon: "i" },
  loading: { bar: "#94a3b8", icon: null },
};

const ToastItem = ({ t, onDismiss }) => {
  const meta = TOAST_META[t.type];
  const [progress, setProgress] = useState(100);
  const [exiting,  setExiting]  = useState(false);
  const rafRef  = useRef(null);
  const startTs = useRef(Date.now());

  const dismiss = useCallback(() => {
    setExiting(true);
    setTimeout(() => onDismiss(t.id), 280);
  }, [t.id, onDismiss]);

  useEffect(() => {
    if (t.persistent) return;
    const tick = () => {
      const pct = Math.max(0, 100 - ((Date.now() - startTs.current) / TOAST_DURATION) * 100);
      setProgress(pct);
      if (pct > 0) rafRef.current = requestAnimationFrame(tick);
      else dismiss();
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [t.persistent, dismiss]);

  return (
    <div style={{ position: "relative", overflow: "hidden", background: "#fff", borderRadius: 10, borderLeft: `4px solid ${meta.bar}`, boxShadow: "0 4px 20px rgba(0,0,0,.10), 0 0 0 1px rgba(0,0,0,.06)", minWidth: 300, maxWidth: 360, animation: exiting ? "dg-alOut .28s ease forwards" : "dg-alIn .28s cubic-bezier(.34,1.56,.64,1) forwards" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "13px 14px 14px" }}>
        <div style={{ flexShrink: 0, width: 24, height: 24, borderRadius: "50%", background: `${meta.bar}18`, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>
          {t.type === "loading"
            ? <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ animation: "dg-spin .85s linear infinite" }}><circle cx="6.5" cy="6.5" r="5" stroke={meta.bar} strokeWidth="2" strokeDasharray="8 20" strokeLinecap="round" /></svg>
            : <span style={{ fontSize: 11, fontWeight: 800, color: meta.bar, lineHeight: 1 }}>{meta.icon}</span>}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 13.5, fontWeight: 650, color: "#0f172a", lineHeight: 1.35 }}>{t.title}</p>
          {t.msg && <p style={{ margin: "3px 0 0", fontSize: 12.5, color: "#64748b", lineHeight: 1.5 }}>{t.msg}</p>}
        </div>
        <button onClick={dismiss} style={{ background: "none", border: "none", cursor: "pointer", color: "#cbd5e1", padding: 2, flexShrink: 0, display: "flex", borderRadius: 4 }}
          onMouseEnter={(e) => e.currentTarget.style.color = "#94a3b8"}
          onMouseLeave={(e) => e.currentTarget.style.color = "#cbd5e1"}>
          <CloseIcon />
        </button>
      </div>
      {!t.persistent && (
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: "#f1f5f9" }}>
          <div style={{ height: "100%", width: `${progress}%`, background: meta.bar, transition: "width .1s linear", borderRadius: "0 2px 0 0" }} />
        </div>
      )}
    </div>
  );
};

const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);
  _dgToastSetter = setToasts;
  const dismiss = useCallback((id) => setToasts((p) => p.filter((t) => t.id !== id)), []);
  if (!toasts.length) return null;
  return (
    <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 9999, display: "flex", flexDirection: "column", gap: 10, alignItems: "flex-end" }}>
      {toasts.map((t) => <ToastItem key={t.id} t={t} onDismiss={dismiss} />)}
    </div>
  );
};

const InfoRow = ({ icon, label, value, badge }) => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "7px 0", borderBottom: "1px solid #f8fafc" }}>
    <span style={{ color: "#94a3b8", display: "flex", flexShrink: 0, marginTop: 1 }}>{icon}</span>
    <span style={{ fontSize: 12.5, color: "#94a3b8", fontWeight: 550, minWidth: 140, flexShrink: 0 }}>{label}</span>
    {badge ? badge : (
      <span style={{ fontSize: 13, color: "#1e293b", fontWeight: 500, flex: 1, wordBreak: "break-word" }}>
        {value ?? <span style={{ color: "#cbd5e1" }}>—</span>}
      </span>
    )}
  </div>
);

const SkeletonBlock = ({ w, h = 14 }) => (
  <div style={{ height: h, width: w, borderRadius: 6, background: "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)", backgroundSize: "200% 100%", animation: "dg-shimmer 1.4s infinite" }} />
);

const DesignationSkeleton = () => (
  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <SkeletonBlock w="45%" h={22} />
      <div style={{ display: "flex", gap: 8 }}>
        <SkeletonBlock w={80} h={34} />
        <SkeletonBlock w={80} h={34} />
      </div>
    </div>
    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
      {[1, 2, 3].map((i) => (
        <div key={i} style={{ display: "flex", gap: 10, padding: "8px 0", borderBottom: "1px solid #f8fafc" }}>
          <SkeletonBlock w={16} h={16} />
          <SkeletonBlock w={140} h={13} />
          <SkeletonBlock w="40%" h={13} />
        </div>
      ))}
    </div>
  </div>
);

const ConfirmDeleteModal = ({ onConfirm, onCancel, loading, designationName }) => (
  <div
    style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: 20 }}
    onClick={onCancel}
  >
    <div
      style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 400, padding: 28, boxShadow: "0 24px 64px rgba(0,0,0,.20)", animation: "dg-fadeUp .2s ease" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, textAlign: "center" }}>
        {/* <div style={{ width: 52, height: 52, borderRadius: "50%", background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div> */}
        <div>
          <h3 style={{ margin: "0 0 6px", fontSize: 17, fontWeight: 700, color: "#0f172a" }}>Remove Designation?</h3>
          <p style={{ margin: 0, fontSize: 13.5, color: "#64748b", lineHeight: 1.6 }}>
            This will permanently remove
            {designationName ? <> <strong style={{ color: "#1e293b" }}>"{designationName}"</strong></> : " the designation"}
            {" "}from this vendor's contact person. This action cannot be undone.
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 8, width: "100%" }}>
          <button
            onClick={onCancel}
            disabled={loading}
            style={{ flex: 1, padding: "10px 0", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer", background: "#fff", color: "#374151" }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            style={{ flex: 1, padding: "10px 0", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", background: loading ? "#fca5a5" : "#dc2626", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
          >
            {loading ? <><LoaderIcon size={14} /> Removing…</> : <><TrashIcon /> Remove</>}
          </button>
        </div>
      </div>
    </div>
  </div>
);

const DesignationSearchDropdown = ({ selectedDesignation, onSelect, error }) => {
  const [query,    setQuery]    = useState(selectedDesignation?.name || "");
  const [results,  setResults]  = useState([]);
  const [open,     setOpen]     = useState(false);
  const [fetching, setFetching] = useState(false);
  const containerRef = useRef(null);
  const debounceRef  = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const doSearch = useCallback(async (q) => {
    setFetching(true);
    try {
      const { data } = await designationApi.search(q);
      setResults(Array.isArray(data) ? data : (data?.results ?? data?.data ?? []));
    } catch {
      setResults([]);
    } finally {
      setFetching(false);
    }
  }, []);

  const handleInputChange = (val) => {
    setQuery(val);
    onSelect(null); 
    setOpen(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  const handleFocus = () => {
    setOpen(true);
    if (!results.length) doSearch(query);
  };

  const handleSelect = (item) => {
    setQuery(item.name);
    onSelect(item);
    setOpen(false);
  };

  const displayValue = selectedDesignation ? selectedDesignation.name : query;

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", letterSpacing: ".03em", display: "block", marginBottom: 4 }}>
        Designation <span style={{ color: "#dc2626" }}>*</span>
      </label>
      <div style={{ position: "relative" }}>
        <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }}>
          <SearchIcon />
        </span>
        <input
          value={displayValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={handleFocus}
          placeholder="Search designation…"
          style={{
            width: "100%", padding: "9px 36px 9px 32px",
            border: `1px solid ${error ? "#fca5a5" : "#e2e8f0"}`,
            borderRadius: 8, fontSize: 13, background: "#fff", color: "#1e293b",
            outline: "none", boxSizing: "border-box", transition: "border-color .15s",
          }}
          onFocus2={(e) => e.target.style.borderColor = "#3b82f6"}
          onBlur={(e) => e.target.style.borderColor = error ? "#fca5a5" : "#e2e8f0"}
        />
        <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }}>
          {fetching ? <LoaderIcon size={14} /> : <ChevronDownIcon />}
        </span>
      </div>

      {error && (
        <span style={{ fontSize: 11.5, color: "#dc2626", display: "flex", alignItems: "center", gap: 4, marginTop: 4 }}>
          <AlertTriangleIcon /> {error}
        </span>
      )}

      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,.12)", zIndex: 1000, maxHeight: 220, overflowY: "auto" }}>
          {fetching ? (
            <div style={{ padding: "14px", display: "flex", alignItems: "center", gap: 8, color: "#94a3b8", fontSize: 13 }}>
              <LoaderIcon size={14} /> Searching designations…
            </div>
          ) : results.length === 0 ? (
            <div style={{ padding: "14px", color: "#94a3b8", fontSize: 13, textAlign: "center" }}>
              {query.length > 0 ? "No designations found" : "Type to search designations"}
            </div>
          ) : (
            results.map((item) => (
              <div
                key={item.id}
                onClick={() => handleSelect(item)}
                style={{ padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid #f8fafc", transition: "background .1s" }}
                onMouseEnter={(e) => e.currentTarget.style.background = "#f8fafc"}
                onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
              >
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{item.name}</div>
                {item.code && <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 1 }}>Code: {item.code}</div>}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const DesignationFormModal = ({ currentDesignation, vendorProfileId, onClose, onSuccess }) => {
  const [selected, setSelected] = useState(null);
  const [error,    setError]    = useState("");
  const [saving,   setSaving]   = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; document.removeEventListener("keydown", onKey); };
  }, [onClose]);

  const handleSubmit = async () => {
    if (!selected) { setError("Please select a designation."); return; }
    setError("");
    setSaving(true);
    const loadId = toast.loading("Updating designation…");
    try {
      const { data } = await designationApi.update(vendorProfileId, [selected.id]);
      if (data?.status >= 400) {
        throw new Error(data?.data?.detail || data?.data?.message || "Update failed");
      }
      toast.dismiss(loadId);
      toast.success("Designation Updated", "Contact person designation has been updated successfully.");
      onSuccess(data?.data ?? data);
      onClose();
    } catch (err) {
      toast.dismiss(loadId);
      toast.error("Update Failed", err.message || "Could not update the designation. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}
      onClick={onClose}
    >
      <div
        style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 480, boxShadow: "0 24px 64px rgba(0,0,0,.20)", animation: "dg-fadeUp .2s ease", display: "flex", flexDirection: "column" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px", borderBottom: "1px solid #f1f5f9" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: "linear-gradient(135deg,#f59e0b,#d97706)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <EditIcon />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
                {currentDesignation ? "Change Designation" : "Assign Designation"}
              </h2>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "#94a3b8" }}>
                {currentDesignation
                  ? `Currently: ${currentDesignation.name}`
                  : "Search and select a designation for the contact person"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex", padding: 6, borderRadius: 6 }}
            onMouseEnter={(e) => e.currentTarget.style.color = "#64748b"}
            onMouseLeave={(e) => e.currentTarget.style.color = "#94a3b8"}
          >
            <CloseIcon />
          </button>
        </div>

        <div style={{ padding: "20px 24px" }}>
          <DesignationSearchDropdown
            selectedDesignation={selected}
            onSelect={(item) => { setSelected(item); if (item) setError(""); }}
            error={error}
          />

          {selected && (
            <div style={{ marginTop: 12, padding: "10px 14px", background: "#f0fdf4", borderRadius: 8, border: "1px solid #bbf7d0", display: "flex", alignItems: "center", gap: 8 }}>
              <CheckIcon />
              <span style={{ fontSize: 13, fontWeight: 600, color: "#166534" }}>Selected: {selected.name}</span>
              {selected.code && <span style={{ fontSize: 12, color: "#4ade80", marginLeft: "auto" }}>#{selected.code}</span>}
            </div>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "16px 24px", borderTop: "1px solid #f1f5f9" }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{ padding: "9px 20px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: "pointer", background: "#fff", color: "#374151" }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !selected}
            style={{
              padding: "9px 22px", border: "none", borderRadius: 8, fontSize: 13.5, fontWeight: 600,
              cursor: (saving || !selected) ? "not-allowed" : "pointer",
              background: (saving || !selected) ? "#fcd34d" : "#f59e0b",
              color: "#fff", display: "flex", alignItems: "center", gap: 7,
            }}
          >
            {saving ? <><LoaderIcon size={14} /> Saving…</> : <><CheckIcon /> Update Designation</>}
          </button>
        </div>
      </div>
    </div>
  );
};

const DesignationComponent = ({ vendorProfileId }) => {
  const [designation, setDesignation] = useState(null); // single designation object or null
  const [loading,     setLoading]     = useState(true);
  const [notFound,    setNotFound]    = useState(false);
  const [showForm,    setShowForm]    = useState(false);
  const [showDelete,  setShowDelete]  = useState(false);
  const [deleting,    setDeleting]    = useState(false);

  const fetchData = useCallback(async () => {
    if (!vendorProfileId) return;
    setLoading(true);
    setNotFound(false);
    try {
      const { data } = await designationApi.get(vendorProfileId);

      if (data?.status === 404) { setDesignation(null); setNotFound(true); return; }
      if (data?.status === 401) { toast.error("Session Expired", "Please log in again."); return; }
      if (data?.status === 403) { toast.error("Access Denied", "You don't have permission to view this."); return; }
      if (data?.status >= 400) throw new Error("Load failed");

      // Handle both array and object responses gracefully
      const designations = data?.data?.designations ?? data?.designations ?? (Array.isArray(data?.data) ? data.data : null) ?? (Array.isArray(data) ? data : null);

      if (designations && designations.length > 0) {
        setDesignation(designations[0]);
        setNotFound(false);
      } else if (designations && designations.length === 0) {
        setDesignation(null);
        setNotFound(true);
      } else {
        const single = data?.data ?? data;
        if (single?.id || single?.name) {
          setDesignation(single);
          setNotFound(false);
        } else {
          setDesignation(null);
          setNotFound(true);
        }
      }
    } catch (err) {
      toast.error(
        err?.name === "TypeError" ? "Network Error" : "Failed to Load",
        err?.name === "TypeError" ? "Unable to reach the server." : "Unable to fetch designation. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [vendorProfileId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async () => {
    setDeleting(true);
    const loadId = toast.loading("Removing designation…");
    try {
      const { data } = await designationApi.delete(vendorProfileId);
      if (data?.status >= 400 && data?.status !== 404) throw new Error("Delete failed");
      toast.dismiss(loadId);
      toast.success("Designation Removed", "The designation has been successfully removed.");
      setDesignation(null);
      setNotFound(true);
      setShowDelete(false);
    } catch (err) {
      toast.dismiss(loadId);
      toast.error("Delete Failed", err.message || "Could not remove the designation. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const handleSuccess = (responseData) => {
    const designations =
      responseData?.designations ??
      (Array.isArray(responseData?.data) ? responseData.data : null) ??
      (Array.isArray(responseData) ? responseData : null);

    if (designations && designations.length > 0) {
      setDesignation(designations[0]);
    } else if (responseData?.id || responseData?.name) {
      setDesignation(responseData);
    } else {
      // Re-fetch as a safe fallback
      fetchData();
      return;
    }
    setNotFound(false);
  };

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, sans-serif", color: "#1e293b" }}>
      <style>{`
        @keyframes dg-shimmer  { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes dg-fadeUp   { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:none} }
        @keyframes dg-alIn     { from{opacity:0;transform:translateX(24px) scale(.97)} to{opacity:1;transform:none} }
        @keyframes dg-alOut    { from{opacity:1} to{opacity:0;transform:translateX(28px) scale(.95)} }
        @keyframes dg-spin     { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        .dg-action-btn:hover   { opacity:.85 !important; }
        * { box-sizing: border-box; }
      `}</style>

      <ToastContainer />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        
          <div>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#0f172a" }}>Contact Designation</h2>
            <p style={{ margin: "2px 0 0", fontSize: 12.5, color: "#64748b" }}>Assigned role or title of the contact person</p>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            onClick={fetchData}
            disabled={loading}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", background: "#fff", color: "#374151", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", boxShadow: "0 1px 3px rgba(0,0,0,.05)" }}
            onMouseEnter={(e) => e.currentTarget.style.background = "#f1f5f9"}
            onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
          >
            {loading ? <LoaderIcon size={15} /> : <RefreshIcon />} Refresh
          </button>

          {!loading && notFound && (
            <button
              className="dg-action-btn"
              onClick={() => setShowForm(true)}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", background: "#f59e0b", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "opacity .15s" }}
            >
              <PlusIcon /> Assign Designation
            </button>
          )}

          {!loading && designation && (
            <>
              <button
                className="dg-action-btn"
                onClick={() => setShowForm(true)}
                style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", background: "#f59e0b", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "opacity .15s" }}
              >
                <EditIcon /> Edit
              </button>
              <button
                className="dg-action-btn"
                onClick={() => setShowDelete(true)}
                style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", background: "#fee2e2", color: "#dc2626", border: "1px solid #fecaca", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "opacity .15s" }}
              >
                <TrashIcon /> Delete
              </button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <DesignationSkeleton />
      ) : notFound ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "60px 20px", background: "#fff", borderRadius: 12, border: "1px dashed #e2e8f0", gap: 12, textAlign: "center" }}>
          {/* <div style={{ width: 64, height: 64, borderRadius: "50%", background: "linear-gradient(135deg,#fef3c7,#fde68a)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="1.5">
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
              <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
            </svg>
          </div> */}
          <div>
            <p style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "#374151" }}>No Designation Assigned</p>
            <p style={{ margin: 0, fontSize: 13.5, color: "#94a3b8" }}>This contact person hasn't been assigned a designation yet.</p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 7, padding: "10px 22px", background: "#f59e0b", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}
          >
            <PlusIcon /> Assign Designation
          </button>
        </div>
      ) : designation ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 16, animation: "dg-fadeUp .2s ease" }}>

       

          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.04)" }}>
            <div style={{ padding: "12px 16px", background: "linear-gradient(135deg,#fffbeb,#fff)", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 8 }}>
             
              <span style={{ fontSize: 13.5, fontWeight: 700, color: "#0f172a" }}>Designation Details</span>
            </div>
            <div style={{ padding: "12px 16px" }}>
              <InfoRow
                // icon={<BadgeIcon />}
                label="Designation Name"
                badge={
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 10px", borderRadius: 20, fontSize: 12.5, fontWeight: 700, background: "#fef3c7", color: "#92400e" }}>
                    {designation.name}
                  </span>
                }
              />
            
              {designation.code && (
                <InfoRow
                //  icon={<HashIcon />}
                 label="Code" value={designation.code} />
              )}
              {designation.description && (
                <InfoRow icon={<UserIcon />} 
                label="Description" value={designation.description} />
              )}
            </div>
          </div>
        </div>
      ) : null}

      {showForm && (
        <DesignationFormModal
          currentDesignation={designation}
          vendorProfileId={vendorProfileId}
          onClose={() => setShowForm(false)}
          onSuccess={handleSuccess}
        />
      )}

      {showDelete && (
        <ConfirmDeleteModal
          loading={deleting}
          designationName={designation?.name}
          onConfirm={handleDelete}
          onCancel={() => setShowDelete(false)}
        />
      )}
    </div>
  );
};

export default DesignationComponent;
